// Terrain enrichi (incrément 5) : volets « Réserve », « Photo », « Mesure », « Pièce posée », levée d'une réserve, onglets
// « Réserves » et « Pièces posées » de la page du chantier.
// Téléphone d'abord : chaque volet est un dialogue dont le pied (« Valider ») reste visible sans défiler sur 375 × 667 et 360 × 740 ;
// le contenu défile au-dessus. Le serveur décide seul des droits et de l'affectation du jour : l'écran propose, le serveur tranche.
// Rien d'inventé : une valeur pré-remplie (lieu du poste, ligne « À poser ») est visible et modifiable avant l'envoi. Aucun montant.
// Incrément 6 : chaque saisie passe par la file d'envoi (gardée sur le téléphone d'abord, envoyée s'il y a du réseau) ; une photo
// jointe (réserve, pièce posée) part APRÈS sa saisie et la désigne par son identifiant d'envoi (reserveOp, pieceOp).
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, CloudUpload, ExternalLink, Package, Plus } from 'lucide-react';
import { api } from '../../../lib/api.ts';
import { useReglagesSalaries } from '../../../lib/session.tsx';
import { date } from '../../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, Dialogue, EtatErreur, EtatVide, LienBouton, Panneau, useNotifier } from '../../../components/ui.tsx';
import { UNITES } from '../types.ts';
import { jourHeure, nouvelEnvoi } from './Journal.tsx';
import { ChampPhoto, VignettesPhotos, type RattachementPhoto } from './PhotoChantier.tsx';
import { GARDEE, messageSaisie, operationPhoto, texteSecondaires, useSaisie } from './saisie.ts';
import { NATURES_RESERVE, type CadreSaisie, type ListePieces, type ListeReserves, type NatureReserve, type PiecePosee, type Reserve } from './types.ts';

export type Volet = 'reserve' | 'photo' | 'mesure' | 'piece';
export const VOLETS: Volet[] = ['reserve', 'photo', 'mesure', 'piece'];
export const estVolet = (v: string | null): v is Volet => !!v && (VOLETS as string[]).includes(v);

const chantierDe = (cadre: CadreSaisie) => ({ chantierId: cadre.chantierId, chantierLibelle: cadre.libelle ?? 'Chantier' });

/** Choix d'une étape (regroupées par poste) ; « valeurVide » = aucune étape. */
function ChoixEtape({ cadre, posteId, valeur, changer, libelle = 'Étape (facultatif)', vide = 'Aucune étape précise' }:
  { cadre: CadreSaisie; posteId?: string | null; valeur: string; changer: (v: string) => void; libelle?: string; vide?: string }) {
  const postes = cadre.postes.filter((p) => p.etapes.length && (!posteId || p.id === posteId));
  if (!postes.length) return null;
  return (
    <div className="champ">
      <label className="champ-libelle" htmlFor="tr-choix-etape">{libelle}</label>
      <select id="tr-choix-etape" className="saisie" value={valeur} onChange={(e) => changer(e.target.value)}>
        <option value="">{vide}</option>
        {postes.length === 1
          ? postes[0].etapes.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)
          : postes.map((p) => <optgroup key={p.id} label={p.label}>{p.etapes.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}</optgroup>)}
      </select>
    </div>
  );
}

/** Pied commun : « Fermer » et l'action principale (toujours visible, désactivée tant que la saisie n'est pas complète). */
const pied = (fermer: () => void, valider: () => void, pret: boolean, envoi: boolean, libelle = 'Valider') => (
  <><Bouton onClick={fermer}>Fermer</Bouton><Bouton variante="principal" chargement={envoi} disabled={!pret} onClick={valider}>{libelle}</Bouton></>
);

// ─── Volet « Réserve » (aléa, réserve du client, réserve interne) ─────────────────────────────────────────────
export function VoletReserve({ cadre, fermer }: { cadre: CadreSaisie; fermer: () => void }) {
  const notifier = useNotifier();
  // Photos de chantier désactivées par la société : pas de champ photo (le serveur refuse aussi).
  const { photos: photosActives } = useReglagesSalaries();
  const { envoyer } = useSaisie();
  const [nature, setNature] = useState<NatureReserve>('alea');
  const [texte, setTexte] = useState('');
  const [posteId, setPosteId] = useState(cadre.posteId ?? '');
  const [etapeId, setEtapeId] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [op] = useState(nouvelEnvoi);
  const [opPhoto] = useState(nouvelEnvoi);
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const ch = chantierDe(cadre);
      const libelle = `${NATURES_RESERVE[nature]} : ${texte.slice(0, 80)}`;
      const s = await envoyer([
        { ...ch, clientOpId: op, type: 'reserve', donnees: { nature, texte, posteId: posteId || null, etapeId: etapeId || null }, libelle },
        ...(photo ? [await operationPhoto({ ...ch, fichier: photo, rattachement: { reserveOp: op }, clientOpId: opPhoto, libelle: `Photo de la réserve « ${texte.slice(0, 60)} »` })] : []),
      ], 0);
      if (s.etat === 'refusee') { setErreur(s.message ?? 'Réserve refusée.'); return; }
      notifier(s.etat === 'envoyee' ? 'Réserve ouverte : elle reste visible jusqu’à sa levée.' : `Réserve enregistrée. ${GARDEE}`);
      for (const x of [...s.avertissements, ...texteSecondaires(s)].slice(0, 2)) notifier(x, true);
      fermer();
    } catch (e) { setErreur(messageSaisie(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Ouvrir une réserve" ouvert fermer={fermer} pied={pied(fermer, valider, texte.trim().length >= 3, envoi)}>
      <div className="pile tr-volet">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <fieldset className="pl-cases">
          <legend className="petit">Nature</legend>
          <div className="tr-natures">
            {(Object.keys(NATURES_RESERVE) as NatureReserve[]).map((n) => (
              <label key={n} className={`ch-choix ${nature === n ? 'est-choisi' : ''}`}>
                <input type="radio" name="nature-reserve" checked={nature === n} onChange={() => setNature(n)} />{NATURES_RESERVE[n]}</label>
            ))}
          </div>
        </fieldset>
        <ChampTexte libelle="Ce qui ne va pas" rows={3} value={texte} onChange={(e) => setTexte(e.target.value)} maxLength={2000} autoFocus
          aide="Exemple : support non conforme du lot plâtrerie, accès refusé, matériel manquant." />
        {cadre.postes.length > 1 && (
          <ChampChoix libelle="Où (facultatif)" value={posteId} onChange={(e) => { setPosteId(e.target.value); setEtapeId(''); }} vide="Tout le chantier"
            options={cadre.postes.map((p) => ({ valeur: p.id, libelle: p.label }))} />
        )}
        <ChoixEtape cadre={cadre} posteId={posteId || null} valeur={etapeId} changer={setEtapeId} />
        {photosActives && <ChampPhoto libelle="Photo (facultatif)" photo={photo} changer={setPhoto} />}
      </div>
    </Dialogue>
  );
}

// ─── Volet « Photo » (rattachée à une étape, ou photo du jour du chantier) ──────────────────────────────────────
export function VoletPhoto({ cadre, fermer, etapeId: etapeInitiale }: { cadre: CadreSaisie; fermer: () => void; etapeId?: string | null }) {
  const notifier = useNotifier();
  const { envoyer } = useSaisie();
  const [photo, setPhoto] = useState<File | null>(null);
  const [etapeId, setEtapeId] = useState(etapeInitiale ?? '');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [op] = useState(nouvelEnvoi);
  const valider = async () => {
    if (!photo) return;
    setErreur(null); setEnvoi(true);
    try {
      const etape = cadre.postes.flatMap((p) => p.etapes).find((e) => e.id === etapeId);
      const s = await envoyer([await operationPhoto({ ...chantierDe(cadre), fichier: photo, rattachement: etapeId ? { etapeId } : {}, clientOpId: op,
        libelle: etape ? `Photo de l’étape « ${etape.label} »` : 'Photo du jour du chantier' })]);
      if (s.etat === 'refusee') { setErreur(s.message ?? 'Photo refusée.'); return; }
      notifier(s.etat === 'envoyee' ? 'Photo enregistrée.' : `Photo enregistrée. ${GARDEE}`);
      fermer();
    } catch (e) { setErreur(messageSaisie(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Ajouter une photo" ouvert fermer={fermer} pied={pied(fermer, valider, !!photo, envoi)}>
      <div className="pile tr-volet">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampPhoto libelle="Photo" photo={photo} changer={setPhoto} />
        <ChoixEtape cadre={cadre} valeur={etapeId} changer={setEtapeId} libelle="Rattacher à" vide="Photo du jour du chantier" />
      </div>
    </Dialogue>
  );
}

// ─── Volet « Mesure » (valeur ET unité obligatoires) ────────────────────────────────────────────────────────
const UNITES_MESURE = UNITES.filter((u) => u !== 'autre');

export function VoletMesure({ cadre, fermer }: { cadre: CadreSaisie; fermer: () => void }) {
  const notifier = useNotifier();
  const { envoyer } = useSaisie();
  const [point, setPoint] = useState('');
  const [valeur, setValeur] = useState('');
  const [unite, setUnite] = useState('');
  const [autre, setAutre] = useState('');
  const [etapeId, setEtapeId] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [op] = useState(nouvelEnvoi);
  const uniteFinale = unite === 'autre' ? autre.trim() : unite;
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const s = await envoyer([{ ...chantierDe(cadre), clientOpId: op, type: 'mesure',
        donnees: { point, valeur, unite: uniteFinale, etapeId: etapeId || null, posteId: etapeId ? null : cadre.posteId },
        libelle: `Mesure : ${point.slice(0, 60)} = ${valeur} ${uniteFinale}` }]);
      if (s.etat === 'refusee') { setErreur(s.message ?? 'Mesure refusée.'); return; }
      notifier(s.etat === 'envoyee' ? 'Mesure enregistrée.' : `Mesure enregistrée. ${GARDEE}`);
      for (const x of s.avertissements.slice(0, 2)) notifier(x, true);
      fermer();
    } catch (e) { setErreur(messageSaisie(e)); } finally { setEnvoi(false); }
  };
  const pret = !!point.trim() && /^-?\d+([.,]\d+)?$/.test(valeur.trim().replace(/\s/g, '')) && !!uniteFinale;
  return (
    <Dialogue titre="Noter une mesure" ouvert fermer={fermer} pied={pied(fermer, valider, pret, envoi)}>
      <div className="pile tr-volet">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Ce qui est mesuré" value={point} onChange={(e) => setPoint(e.target.value)} maxLength={300} autoFocus
          aide="Exemple : pression BP circuit 1, tension du bus, température de soufflage." />
        <div className="tr-deux">
          <Champ libelle="Valeur" inputMode="decimal" value={valeur} onChange={(e) => setValeur(e.target.value)} />
          <ChampChoix libelle="Unité (obligatoire)" value={unite} onChange={(e) => setUnite(e.target.value)} vide="Choisir"
            options={[...UNITES_MESURE.map((u) => ({ valeur: u, libelle: u })), { valeur: 'autre', libelle: 'Autre…' }]} />
        </div>
        {unite === 'autre' && <Champ libelle="Unité" value={autre} onChange={(e) => setAutre(e.target.value)} maxLength={20} aide="Exemple : l/min, dB, lux." />}
        <ChoixEtape cadre={cadre} valeur={etapeId} changer={setEtapeId} />
      </div>
    </Dialogue>
  );
}

// ─── Volet « Pièce posée » (crée ou complète la fiche de l'équipement ; remplacement = l'ancien est archivé) ─────────────
type ModePose = 'nouveau' | 'existant' | 'remplacement';
const MODES: Record<ModePose, string> = { nouveau: 'Nouvel équipement', existant: 'Compléter un équipement du site', remplacement: 'Remplacer un équipement' };
interface Emplacements { lieux: { id: string; name: string; kind: string }[]; equipements: { id: string; label: string; reference: string | null }[] }
interface ResultatPose { equipement: { id: string; label: string; cree: boolean; complete: string[] }; remplace: { id: string; label: string } | null; avertissements: string[] }

export function VoletPiece({ cadre, fermer }: { cadre: CadreSaisie; fermer: () => void }) {
  const notifier = useNotifier();
  const { photos: photosActives } = useReglagesSalaries();
  const { envoyer } = useSaisie();
  const [gardee, setGardee] = useState(false);
  const poste = cadre.postes.find((p) => p.id === cadre.posteId) ?? (cadre.postes.length === 1 ? cadre.postes[0] : null);
  const empl = useQuery({ queryKey: ['chantier-emplacements', cadre.chantierId], queryFn: () => api.get<Emplacements>(`/api/chantiers/${cadre.chantierId}/emplacements`) });
  const [mode, setMode] = useState<ModePose>('nouveau');
  const [equipementId, setEquipementId] = useState('');
  const [ligne, setLigne] = useState('');
  const [libelle, setLibelle] = useState('');
  const [fabricant, setFabricant] = useState('');
  const [reference, setReference] = useState('');
  const [serie, setSerie] = useState('');
  const [lieuId, setLieuId] = useState(poste?.lieu_id ?? '');
  const [adresse, setAdresse] = useState('');
  const [etapeId, setEtapeId] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState<ResultatPose | null>(null);
  const [op] = useState(nouvelEnvoi);
  const [opPhoto] = useState(nouvelEnvoi);
  const aPoser = poste?.a_poser ?? [];
  // Ligne « À poser » choisie : pré-remplit désignation, fabricant et référence (visibles et modifiables).
  const choisirLigne = (v: string) => {
    setLigne(v);
    const a = aPoser[Number(v)];
    if (!a) return;
    setLibelle(a.label); setFabricant(a.manufacturer ?? ''); setReference(a.reference ?? '');
  };
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const ch = chantierDe(cadre);
      const designation = libelle || equipements.find((x) => x.id === equipementId)?.label || 'équipement';
      const s = await envoyer([
        { ...ch, clientOpId: op, type: 'piece', libelle: `Pièce posée : ${designation}${serie ? ` (n° ${serie})` : ''}`,
          donnees: { mode, equipementId: mode === 'nouveau' ? null : equipementId || null, libelle: libelle || null, fabricant: fabricant || null,
            reference: reference || null, numeroSerie: serie || null, adresseReseau: adresse || null, lieuId: lieuId || null,
            etapeId: etapeId || null, posteId: etapeId ? null : poste?.id ?? null } },
        ...(photo ? [await operationPhoto({ ...ch, fichier: photo, rattachement: { pieceOp: op }, clientOpId: opPhoto, libelle: `Photo de la plaque — ${designation}` })] : []),
      ], 0);
      if (s.etat === 'refusee') { setErreur(s.message ?? 'Pièce posée refusée.'); return; }
      if (s.etat === 'en_attente') { notifier(`Pièce posée enregistrée. ${GARDEE}`); setGardee(true); return; }
      if (!s.verdict?.equipement) { notifier('Pièce posée enregistrée.'); fermer(); return; }
      setResultat({ equipement: s.verdict.equipement, remplace: s.verdict.remplace ?? null, avertissements: [...s.avertissements, ...texteSecondaires(s)] });
      notifier('Pièce posée enregistrée.');
    } catch (e) { setErreur(messageSaisie(e)); } finally { setEnvoi(false); }
  };
  if (gardee) {
    return (
      <Dialogue titre="Pièce posée enregistrée" ouvert fermer={fermer} pied={<Bouton variante="principal" onClick={fermer}>Fermer</Bouton>}>
        <div className="pile tr-volet">
          <Alerte ton="info" titre="Gardée sur ce téléphone">
            <CloudUpload size={16} aria-hidden /> Pas de réseau : la pose partira automatiquement dès son retour. La fiche de l’équipement sera alors créée ou
            complétée, et le compte rendu apparaîtra dans le carnet de l’équipement.
          </Alerte>
        </div>
      </Dialogue>
    );
  }
  if (resultat) {
    const e = resultat.equipement;
    return (
      <Dialogue titre="Pièce posée enregistrée" ouvert fermer={fermer}
        pied={<><Bouton onClick={fermer}>Fermer</Bouton>
          <Link className="btn btn-principal" to={`/sites/equipements/${e.id}`} onClick={fermer}><ExternalLink aria-hidden />Ouvrir la fiche</Link></>}>
        <div className="pile tr-volet">
          <Alerte ton="succes" titre={e.cree ? `Fiche « ${e.label} » créée` : `Fiche « ${e.label} » complétée`}>
            {e.cree ? 'Date d’installation et origine (ce chantier) enregistrées.'
              : e.complete.length ? `Champs complétés : ${e.complete.join(', ')}.` : 'Aucun champ vide à compléter : la fiche est inchangée, la pose est enregistrée.'}
            {resultat.remplace && <> « {resultat.remplace.label} » est archivé et relié au nouvel équipement.</>}
          </Alerte>
          {resultat.avertissements.map((a, i) => <Alerte key={i} ton="ambre">{a}</Alerte>)}
          <p className="petit secondaire" style={{ margin: 0 }}>Le carnet de l’équipement montre la pose, avec le lien vers la page du jour.</p>
        </div>
      </Dialogue>
    );
  }
  const pret = (mode === 'nouveau' ? !!libelle.trim() : !!equipementId) && !envoi;
  const equipements = empl.data?.equipements ?? [];
  return (
    <Dialogue titre="Pièce posée" ouvert fermer={fermer} pied={pied(fermer, valider, pret, envoi)}>
      <div className="pile tr-volet">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampChoix libelle="Ce que vous posez" value={mode} onChange={(e) => setMode(e.target.value as ModePose)}
          options={(Object.keys(MODES) as ModePose[]).map((m) => ({ valeur: m, libelle: MODES[m] }))} />
        {mode !== 'nouveau' && (
          <ChampChoix libelle={mode === 'existant' ? 'Équipement à compléter' : 'Équipement remplacé (il sera archivé)'} value={equipementId}
            onChange={(e) => setEquipementId(e.target.value)} vide={empl.isLoading ? 'Chargement…' : equipements.length ? 'Choisir' : 'Aucun équipement sur ce site'}
            options={equipements.map((x) => ({ valeur: x.id, libelle: x.reference ? `${x.label} (${x.reference})` : x.label }))} />
        )}
        {aPoser.length > 0 && (
          <ChampChoix libelle="Depuis la liste « À poser » (facultatif)" value={ligne} onChange={(e) => choisirLigne(e.target.value)} vide="—"
            options={aPoser.map((a, i) => ({ valeur: String(i), libelle: `${a.label}${a.reference ? ` (${a.reference})` : ''}` }))} />
        )}
        <Champ libelle={mode === 'nouveau' ? 'Désignation' : 'Désignation (facultatif)'} value={libelle} onChange={(e) => setLibelle(e.target.value)} maxLength={300}
          aide={mode === 'remplacement' ? 'Vide : le nouvel équipement reprend le nom de l’ancien.' : undefined} />
        <Champ libelle="Numéro de série" mono value={serie} onChange={(e) => setSerie(e.target.value)} maxLength={120} aide="Relevé sur la plaque (photo ci-dessous)." />
        <div className="tr-deux">
          <Champ libelle="Fabricant" value={fabricant} onChange={(e) => setFabricant(e.target.value)} maxLength={120} />
          <Champ libelle="Référence" mono value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} />
        </div>
        {(empl.data?.lieux.length ?? 0) > 0 && (
          <ChampChoix libelle="Emplacement" value={lieuId} onChange={(e) => setLieuId(e.target.value)}
            vide={mode === 'remplacement' ? 'Celui de l’équipement remplacé' : 'Non précisé'}
            options={empl.data!.lieux.map((l) => ({ valeur: l.id, libelle: l.name }))} />
        )}
        <Champ libelle="Adresse bus ou réseau (facultatif)" mono value={adresse} onChange={(e) => setAdresse(e.target.value)} maxLength={120} aide="Exemple : 1.1.12, 192.168.1.20, Modbus 14." />
        <ChoixEtape cadre={cadre} posteId={poste?.id ?? null} valeur={etapeId} changer={setEtapeId} />
        {photosActives && <ChampPhoto libelle="Photo de la plaque (facultatif)" photo={photo} changer={setPhoto} />}
      </div>
    </Dialogue>
  );
}

/** Volet « Photo » seulement si la société n'a pas désactivé les photos ; sinon un message (aucun envoi possible). */
function VoletPhotoSiActive({ cadre, fermer }: { cadre: CadreSaisie; fermer: () => void }) {
  const { photos } = useReglagesSalaries();
  if (photos) return <VoletPhoto cadre={cadre} fermer={fermer} />;
  return (
    <Dialogue titre="Photos désactivées" ouvert fermer={fermer} pied={<Bouton variante="principal" onClick={fermer}>Fermer</Bouton>}>
      <p style={{ margin: 0 }}>Votre entreprise a désactivé les photos de chantier. Décrivez ce que vous vouliez montrer dans une note, une consigne ou une réserve.</p>
    </Dialogue>
  );
}

/** Le volet demandé, pour un cadre de saisie donné. */
export function VoletTerrain({ volet, cadre, fermer }: { volet: Volet; cadre: CadreSaisie; fermer: () => void }) {
  if (volet === 'reserve') return <VoletReserve cadre={cadre} fermer={fermer} />;
  if (volet === 'photo') return <VoletPhotoSiActive cadre={cadre} fermer={fermer} />;
  if (volet === 'mesure') return <VoletMesure cadre={cadre} fermer={fermer} />;
  return <VoletPiece cadre={cadre} fermer={fermer} />;
}

// ─── Lever une réserve (nouvelle ligne : par qui, quand, comment) ──────────────────────────────────────────────
export function DialogueLever({ chantierId, chantierLibelle, reserve, fermer }: { chantierId: string; chantierLibelle?: string; reserve: Reserve; fermer: () => void }) {
  const notifier = useNotifier();
  const { envoyer } = useSaisie();
  const [note, setNote] = useState('');
  const [sansObjet, setSansObjet] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [op] = useState(nouvelEnvoi);
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const s = await envoyer([{ chantierId, chantierLibelle: chantierLibelle ?? 'Chantier', clientOpId: op, type: 'levee',
        donnees: { reserveId: reserve.id, note, sansObjet }, libelle: `Levée de la réserve « ${reserve.texte.slice(0, 60)} »` }]);
      if (s.etat === 'refusee') { setErreur(s.message ?? 'Levée refusée.'); return; }
      notifier(s.etat === 'en_attente' ? `Levée enregistrée. ${GARDEE}`
        : s.verdict?.statut === 'sans_effet' && s.verdict.message ? s.verdict.message : 'Réserve levée : elle reste dans l’historique du chantier.');
      fermer();
    } catch (e) { setErreur(messageSaisie(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Lever la réserve" ouvert fermer={fermer} pied={pied(fermer, valider, note.trim().length >= 5, envoi, 'Lever la réserve')}>
      <div className="pile tr-volet">
        <p className="petit" style={{ margin: 0 }}><strong>{reserve.nature_libelle}</strong> — {reserve.texte}</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampTexte libelle="Comment elle a été levée" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} autoFocus
          aide="Exemple : support repris par le plaquiste, contrôle fait." />
        <label className={`ch-choix ${sansObjet ? 'est-choisi' : ''}`}>
          <input type="checkbox" checked={sansObjet} onChange={(e) => setSansObjet(e.target.checked)} />Sans objet (saisie erronée)</label>
      </div>
    </Dialogue>
  );
}

// ─── Une réserve (carte) ─────────────────────────────────────────────────────────────────────────────────
function CarteReserve({ chantierId, r, lever }: { chantierId: string; r: Reserve; lever?: (r: Reserve) => void }) {
  const ou = [r.poste?.label, r.etape?.label, r.equipement?.label, r.lieu?.label].filter(Boolean).join(' — ');
  return (
    <li className={`tr-reserve ${r.statut === 'levee' ? 'tr-levee' : ''}`}>
      <div className="tr-reserve-texte">
        <span className="rangee" style={{ gap: 6 }}>
          <Badge ton={r.statut === 'ouverte' ? 'ambre' : 'succes'} icone={r.statut === 'ouverte' ? <AlertTriangle size={14} aria-hidden /> : <CheckCircle2 size={14} aria-hidden />}>
            {r.statut === 'ouverte' ? r.nature_libelle : r.levee?.sans_objet ? 'Levée — sans objet' : 'Levée'}</Badge>
          {r.statut === 'levee' && <span className="petit discret">{r.nature_libelle}</span>}
        </span>
        <span className="tr-texte">{r.texte}</span>
        {ou && <span className="petit secondaire">{ou}</span>}
        <span className="petit secondaire">Ouverte par {r.moi ? 'vous' : r.par.nom}, {jourHeure(r.le)}</span>
        {r.levee && <span className="petit">Levée par {r.levee.par.nom} le {jourHeure(r.levee.le)} : {r.levee.note}</span>}
        <VignettesPhotos chantierId={chantierId} photos={r.photos} libelle={r.texte.slice(0, 60)} />
      </div>
      {r.levable && lever && <Bouton compact onClick={() => lever(r)}>Lever</Bouton>}
    </li>
  );
}

export function ListeReservesJour({ chantierId, reserves, lever }: { chantierId: string; reserves: Reserve[]; lever?: (r: Reserve) => void }) {
  return <ul className="tr-liste">{reserves.map((r) => <CarteReserve key={r.id} chantierId={chantierId} r={r} lever={lever} />)}</ul>;
}

// ─── Onglet « Réserves » de la page du chantier ───────────────────────────────────────────────────────────────
export function PanneauReserves({ cadre, voletOuvert, ouvrir, fermer }: { cadre: CadreSaisie; voletOuvert: boolean; ouvrir: () => void; fermer: () => void }) {
  const q = useQuery({ queryKey: ['chantier-reserves', cadre.chantierId], queryFn: () => api.get<ListeReserves>(`/api/chantiers/${cadre.chantierId}/reserves`) });
  const [lever, setLever] = useState<Reserve | null>(null);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const v = q.data!;
  return (
    <div className="pile">
      <Panneau titre={`Réserves ouvertes — ${v.ouvertes.length}`}>
        {v.ouvertes.length === 0
          ? <p className="petit secondaire" style={{ margin: 0 }}>Aucune réserve ouverte sur ce chantier.</p>
          : <ListeReservesJour chantierId={cadre.chantierId} reserves={v.ouvertes} lever={setLever} />}
      </Panneau>
      {v.levees.length > 0 && (
        <Panneau titre={`Réserves levées — ${v.levees.length}`}>
          <ListeReservesJour chantierId={cadre.chantierId} reserves={v.levees} />
        </Panneau>
      )}
      {v.droits.ouvrir && (
        <div className="barre-actions barre-collante">
          <span className="barre-collante-resume petit secondaire masque-etroit">Aléa, réserve du client ou interne : elle reste visible jusqu’à sa levée.</span>
          <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={ouvrir}>Ouvrir une réserve</Bouton>
        </div>
      )}
      {voletOuvert && v.droits.ouvrir && <VoletReserve cadre={cadre} fermer={fermer} />}
      {lever && <DialogueLever chantierId={cadre.chantierId} chantierLibelle={cadre.libelle} reserve={lever} fermer={() => setLever(null)} />}
    </div>
  );
}

// ─── Pièces posées (onglet de la page du chantier et page du jour) ───────────────────────────────────────────────
function CartePiece({ chantierId, p, avecJour }: { chantierId: string; p: PiecePosee; avecJour?: boolean }) {
  return (
    <li className="tr-piece">
      <div className="tr-reserve-texte">
        <span className="rangee" style={{ gap: 6 }}>
          <strong className="tr-texte">{p.label}</strong>
          <Badge ton={p.cree ? 'succes' : 'info'} icone={false}>{p.remplace ? 'Remplacement' : p.cree ? 'Fiche créée' : 'Fiche complétée'}</Badge>
          {p.equipement.archive && <Badge ton="neutre" icone={false}>Archivé depuis</Badge>}
        </span>
        <span className="petit">{p.numero_serie ? <>N° de série <span className="mono">{p.numero_serie}</span></> : <span className="discret">N° de série non relevé</span>}
          {p.reference ? <> · réf. <span className="mono">{p.reference}</span></> : null}{p.fabricant ? ` · ${p.fabricant}` : ''}</span>
        {(p.lieu || p.adresse_reseau) && <span className="petit secondaire">{[p.lieu?.label, p.adresse_reseau ? `adresse ${p.adresse_reseau}` : null].filter(Boolean).join(' — ')}</span>}
        {(p.poste || p.etape) && <span className="petit secondaire">{[p.poste?.label, p.etape?.label].filter(Boolean).join(' — ')}</span>}
        {p.remplace && <span className="petit secondaire">Remplace « <Link to={`/sites/equipements/${p.remplace.id}`}>{p.remplace.label}</Link> » (archivé)</span>}
        <span className="petit secondaire">Posé par {p.par.nom}, {jourHeure(p.le)}</span>
        <VignettesPhotos chantierId={chantierId} photos={p.photos} libelle={p.label} />
      </div>
      <div className="tr-piece-liens">
        <LienBouton compact icone={<Package aria-hidden />} to={`/sites/equipements/${p.equipement.id}`}>Fiche de l’équipement</LienBouton>
        {avecJour && <LienBouton compact variante="fantome" to={`/interventions/chantiers/${chantierId}/jour/${p.jour}`}>Page du jour</LienBouton>}
      </div>
    </li>
  );
}

export function ListePiecesPosees({ chantierId, pieces, avecJour }: { chantierId: string; pieces: PiecePosee[]; avecJour?: boolean }) {
  return <ul className="tr-liste">{pieces.map((p) => <CartePiece key={p.id} chantierId={chantierId} p={p} avecJour={avecJour} />)}</ul>;
}

export function PanneauPieces({ cadre, voletOuvert, ouvrir, fermer }: { cadre: CadreSaisie; voletOuvert: boolean; ouvrir: () => void; fermer: () => void }) {
  const q = useQuery({ queryKey: ['chantier-pieces', cadre.chantierId], queryFn: () => api.get<ListePieces>(`/api/chantiers/${cadre.chantierId}/pieces`) });
  const parJour = useMemo(() => {
    const m = new Map<string, PiecePosee[]>();
    for (const p of q.data?.pieces ?? []) m.set(p.jour, [...(m.get(p.jour) ?? []), p]);
    return [...m.entries()];
  }, [q.data]);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const v = q.data!;
  return (
    <div className="pile">
      {v.pieces.length === 0 ? (
        <EtatVide titre="Aucune pièce posée enregistrée" texte="Chaque pièce posée crée ou complète la fiche de l’équipement du site (numéro de série, emplacement, date d’installation)." />
      ) : parJour.map(([jour, pieces]) => (
        <Panneau key={jour} titre={<Link to={`/interventions/chantiers/${cadre.chantierId}/jour/${jour}`}>Posées le {date(jour)}</Link>}>
          <ListePiecesPosees chantierId={cadre.chantierId} pieces={pieces} avecJour />
        </Panneau>
      ))}
      {v.droits.poser && (
        <div className="barre-actions barre-collante">
          <span className="barre-collante-resume petit secondaire masque-etroit">Numéro de série, emplacement : la fiche de l’équipement est créée ou complétée.</span>
          <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={ouvrir}>Pièce posée</Bouton>
        </div>
      )}
      {voletOuvert && v.droits.poser && <VoletPiece cadre={cadre} fermer={fermer} />}
    </div>
  );
}

export type { RattachementPhoto };
